import { useRef, useState } from 'react';
import {
  packageOptions,
  trainerOptions,
  getMemberInitials,
} from './memberData';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import './members.css';

const emptyForm = {
  photo: null,
  photoPreview: null,
  firstName: '',
  lastName: '',
  mobile: '',
  alternateMobile: '',
  landline: '',
  cnic: '',
  email: '',
  dob: '',
  gender: 'male',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  country: 'Pakistan',
  zipCode: '',
  emergencyContact: '',
  package: 'Basic',
  trainer: 'Unassigned',
  branch: '',
  joiningDate: '',
  status: 'active',
};

function MemberForm({ initialData, onSubmit, onCancel, submitLabel, branchOptions = [] }) {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ ...emptyForm, ...initialData });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, photo: file, photoPreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setForm((prev) => ({ ...prev, photo: null, photoPreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = String(form.firstName || '').trim();
    const phone = String(form.mobile || '').trim();
    if (!name) {
      e.target.querySelector('#firstName')?.focus();
      return;
    }
    if (!phone) {
      e.target.querySelector('#mobile')?.focus();
      return;
    }
    onSubmit?.(form);
  };

  return (
    <form className="subscription-form" onSubmit={handleSubmit} noValidate>
      <div className="member-form-grid">
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="firstName">First Name *</label>
          <input id="firstName" name="firstName" className="owner-form-input" value={form.firstName} onChange={handleChange} required />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="lastName">Last Name</label>
          <input id="lastName" name="lastName" className="owner-form-input" value={form.lastName} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="mobile">Mobile No *</label>
          <input id="mobile" name="mobile" type="tel" className="owner-form-input" value={form.mobile} onChange={handleChange} required />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="alternateMobile">Alternate Mobile Number</label>
          <input id="alternateMobile" name="alternateMobile" type="tel" className="owner-form-input" value={form.alternateMobile} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="landline">Landline</label>
          <input id="landline" name="landline" type="tel" className="owner-form-input" value={form.landline} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="cnic">CNIC</label>
          <input
            id="cnic"
            name="cnic"
            className="owner-form-input"
            value={form.cnic}
            onChange={handleChange}
            placeholder="e.g. 42101-1234567-1"
            inputMode="numeric"
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="email">Email</label>
          <input id="email" name="email" type="email" className="owner-form-input" value={form.email} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="dob">Date of Birth</label>
          <input id="dob" name="dob" type="date" className="owner-form-input" value={form.dob} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="gender">Gender</label>
          <select id="gender" name="gender" className="owner-form-select" value={form.gender} onChange={handleChange}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="owner-form-group member-form-full">
          <label className="owner-form-label" htmlFor="addressLine1">Address Line 1</label>
          <input id="addressLine1" name="addressLine1" className="owner-form-input" value={form.addressLine1} onChange={handleChange} />
        </div>
        <div className="owner-form-group member-form-full">
          <label className="owner-form-label" htmlFor="addressLine2">Address Line 2</label>
          <input id="addressLine2" name="addressLine2" className="owner-form-input" value={form.addressLine2} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="city">City</label>
          <input id="city" name="city" className="owner-form-input" value={form.city} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="state">State</label>
          <input id="state" name="state" className="owner-form-input" value={form.state} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="country">Country</label>
          <input id="country" name="country" className="owner-form-input" value={form.country} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="zipCode">Zip Code</label>
          <input id="zipCode" name="zipCode" className="owner-form-input" value={form.zipCode} onChange={handleChange} />
        </div>
      </div>

      <div className="member-photo-upload member-photo-upload-end">
        <div className="member-photo-preview">
          {form.photoPreview ? (
            <img src={form.photoPreview} alt="Member" />
          ) : (
            getMemberInitials(form)
          )}
        </div>
        <div className="member-photo-actions">
          <label className="owner-form-label">Profile Photo</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="member-file-input"
            onChange={handlePhotoSelect}
          />
          <button
            type="button"
            className="owner-btn owner-btn-secondary owner-btn-sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload Photo
          </button>
          {form.photoPreview && (
            <button
              type="button"
              className="owner-btn owner-btn-secondary owner-btn-sm"
              onClick={handleRemovePhoto}
            >
              Remove Photo
            </button>
          )}
          <p className="member-photo-hint">PNG, JPG or WebP. Recommended: square image</p>
        </div>
      </div>

      <div className="member-form-grid member-form-grid-section">
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="emergencyContact">Emergency Contact</label>
          <input id="emergencyContact" name="emergencyContact" type="tel" className="owner-form-input" value={form.emergencyContact} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="package">Package</label>
          <select id="package" name="package" className="owner-form-select" value={form.package} onChange={handleChange}>
            {packageOptions.map((pkg) => (
              <option key={pkg} value={pkg}>{pkg}</option>
            ))}
          </select>
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="trainer">Trainer</label>
          <select id="trainer" name="trainer" className="owner-form-select" value={form.trainer} onChange={handleChange}>
            {trainerOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="branch">Branch</label>
          <select id="branch" name="branch" className="owner-form-select" value={form.branch || ''} onChange={handleChange}>
            <option value="">Select branch</option>
            {branchOptions.map((branch) => (
              <option key={branch} value={branch}>{branch}</option>
            ))}
          </select>
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="joiningDate">Joining Date</label>
          <input id="joiningDate" name="joiningDate" type="date" className="owner-form-input" value={form.joiningDate} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="status">Status</label>
          <select id="status" name="status" className="owner-form-select" value={form.status} onChange={handleChange}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="subscription-form-actions">
        <button type="submit" className="owner-btn owner-btn-primary">{submitLabel}</button>
        {onCancel && (
          <button type="button" className="owner-btn owner-btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

export default MemberForm;
